import { useState, useCallback, useEffect } from 'react';
import type { RefetchFnDynamic } from 'react-relay';
import type { KeyType } from 'react-relay/relay-hooks/helpers';
import type { OperationType } from 'relay-runtime';
import type { UpdatedIssueType } from '@atlassian/jira-issue-type-config-controllers/src/controllers/use-update-issue-type/index.tsx';
import type { CreatedIssueType } from '@atlassian/jira-issue-type-config-controllers/src/controllers/create-issue-type/useCreateIssueType.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Option, NullableOption } from '../types.tsx';

const PENDING_CREATED_ISSUE_TYPE_TIMEOUT_MS = 10000; // 10 seconds

export type UseManageIssueTypeCallbacksParams<
	TQuery extends OperationType,
	TKey extends KeyType | null,
> = {
	onRefetch: RefetchFnDynamic<TQuery, TKey>;
	onGetRefetchVariables: (searchBy: string) => TQuery['variables'];
	selectFieldOptions: Option[];
	onChange?: (value: NullableOption) => void;
};

export type UseManageIssueTypeCallbacksReturn = {
	onIssueTypeUpdated: (updatedIssueType?: UpdatedIssueType) => void;
	onIssueTypeCreated: (createdIssueType: CreatedIssueType) => void;
};

/**
 * Hook that manages issue type creation and update callbacks.
 * Handles refetching data, selecting the appropriate option, and managing
 * pending state for newly created issue types.
 */
export function useManageIssueTypeCallbacks<
	TQuery extends OperationType,
	TKey extends KeyType | null,
>({
	onRefetch,
	onGetRefetchVariables,
	selectFieldOptions,
	onChange,
}: UseManageIssueTypeCallbacksParams<TQuery, TKey>): UseManageIssueTypeCallbacksReturn {
	const [pendingCreatedIssueType, setPendingCreatedIssueType] = useState<CreatedIssueType | null>(
		null,
	);

	// Watch for created issue type to appear in options after refetch in order to select it
	useEffect(() => {
		if (!pendingCreatedIssueType) {
			return;
		}

		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
		const foundOption = selectFieldOptions.find(
			(option) => option.issueTypeId === pendingCreatedIssueType.issueTypeId,
		);

		if (foundOption) {
			onChange?.(foundOption);
			setPendingCreatedIssueType(null);
			return;
		}

		// Issue type not found yet - set timeout to clear pending state after 10 seconds
		// This handles the case where refetch fails or the issue type never appears
		const timeout = setTimeout(() => {
			setPendingCreatedIssueType(null);
		}, PENDING_CREATED_ISSUE_TYPE_TIMEOUT_MS);

		return () => {
			clearTimeout(timeout);
		};
	}, [pendingCreatedIssueType, selectFieldOptions, onChange]);

	const onIssueTypeUpdated = useCallback(
		(updatedIssueType?: UpdatedIssueType) => {
			onRefetch(onGetRefetchVariables(''), { fetchPolicy: 'network-only' });

			const issueTypeOptionToSelect = selectFieldOptions.find(
				(option) => option.issueTypeId === updatedIssueType?.issueTypeId,
			);

			onChange?.(issueTypeOptionToSelect ?? null);
		},
		[onRefetch, onGetRefetchVariables, selectFieldOptions, onChange],
	);

	const onIssueTypeCreated = useCallback(
		(createdIssueType: CreatedIssueType) => {
			if (fg('work_type_inline_config_nin_icc_fast_follow')) {
				setPendingCreatedIssueType(createdIssueType);
			}
			onRefetch(onGetRefetchVariables(''), { fetchPolicy: 'network-only' });
		},
		[onGetRefetchVariables, onRefetch],
	);

	return {
		onIssueTypeUpdated,
		onIssueTypeCreated,
	};
}
